import { useState } from "react";
import axios from "axios";
import { Mail, MapPin, Send, CheckCircle2, AlertCircle } from "lucide-react";
import { SITE } from "../lib/site";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState({ loading: false, ok: false, error: null });

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setStatus({ loading: true, ok: false, error: null });
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({ loading: false, ok: true, error: null });
      setForm({ name: "", email: "", subject: "", message: "" });
      toast.success("Message sent — we'll be in touch soon!");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      setStatus({ loading: false, ok: false, error: typeof msg === "string" ? msg : "Submit failed" });
      toast.error(typeof msg === "string" ? msg : "Submit failed");
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#050505] py-24 md:py-32 border-b border-white/10"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 md:gap-16">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-12 bg-[#FF2222]" />
            <span className="text-[11px] uppercase tracking-[0.3em] text-white/60">Get in Touch</span>
          </div>
          <h2 className="font-display uppercase text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
            Start a<br /> <span className="text-[#FF2222]">Custom Order</span>
          </h2>
          <p className="mt-6 text-white/65 text-base md:text-lg leading-relaxed max-w-md">
            Bridal parties, team uniforms, family events, business merch — tell us what you need and
            we'll get back to you with a quote.
          </p>

          <div className="mt-10 space-y-5 border-t border-white/10 pt-8">
            <a
              data-testid="contact-email-link"
              href={`mailto:${SITE.email}`}
              className="flex items-start gap-4 group"
            >
              <div className="bg-white/5 group-hover:bg-[#FF2222] p-3 transition-colors">
                <Mail size={18} className="text-white" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 mb-1">Email</div>
                <div className="text-white group-hover:text-[#FF2222] transition-colors break-all">{SITE.email}</div>
              </div>
            </a>
            <div className="flex items-start gap-4">
              <div className="bg-white/5 p-3">
                <MapPin size={18} className="text-white" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 mb-1">Studio</div>
                <div className="text-white">{SITE.location}</div>
                <div className="text-white/50 text-xs mt-1">Serving Northern Virginia & beyond</div>
              </div>
            </div>
          </div>
        </div>

        <div className="md:col-span-7">
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="border border-white/10 bg-[#0a0a0a] p-6 md:p-10 space-y-6"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="text-[10px] uppercase tracking-[0.3em] text-white/50 block mb-2">Name</label>
                <input
                  data-testid="contact-input-name"
                  type="text"
                  value={form.name}
                  onChange={handle("name")}
                  placeholder="Your name"
                  className="iza-input w-full"
                  required
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.3em] text-white/50 block mb-2">Email</label>
                <input
                  data-testid="contact-input-email"
                  type="email"
                  value={form.email}
                  onChange={handle("email")}
                  placeholder="you@email.com"
                  className="iza-input w-full"
                  required
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.3em] text-white/50 block mb-2">Subject</label>
              <input
                data-testid="contact-input-subject"
                type="text"
                value={form.subject}
                onChange={handle("subject")}
                placeholder="Bachelorette tees, team merch, etc."
                className="iza-input w-full"
              />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.3em] text-white/50 block mb-2">Project Details</label>
              <textarea
                data-testid="contact-input-message"
                rows={5}
                value={form.message}
                onChange={handle("message")}
                placeholder="Quantity, design ideas, deadline..."
                className="iza-input w-full resize-none"
                required
              />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
              <div className="text-[11px] uppercase tracking-[0.2em] text-white/40">
                We reply within 1–2 business days
              </div>
              <button
                data-testid="contact-submit-btn"
                type="submit"
                disabled={status.loading}
                className="bg-[#FF2222] hover:bg-white hover:text-black text-white uppercase font-bold tracking-wider text-xs px-8 py-4 inline-flex items-center gap-3 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status.loading ? "Sending..." : (<>Send Message <Send size={14} /></>)}
              </button>
            </div>

            {status.ok && (
              <div data-testid="contact-success" className="flex items-center gap-2 text-green-400 text-sm">
                <CheckCircle2 size={16} /> Got it! We'll be in touch soon.
              </div>
            )}
            {status.error && (
              <div data-testid="contact-error" className="flex items-center gap-2 text-[#FF6666] text-sm">
                <AlertCircle size={16} /> {status.error}
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};
